import { ROOMSSTATE, SECURESTATE, USERSSTATE } from '.'
import { type Participant, type StreamType } from '../../types'
import { Message, TipMessage, TransitionType } from '../../types/state'
import { getSafeNextOrPreviousSurrogate } from '../actions/getNextOrPreviousModel'
import { loadUserIfNeeded } from '../actions/loadUserIfNeeded'
import { setLoungeSurrogateURL } from '../actions/nav'
import { streamPublicShow, unsubscribeRoom } from '../actions/room'
import { saveSettings } from '../actions/settings'
import { SettingsType } from '../constants/enums'
import {
    addToLoungeRecentModelsSession,
    calculateLoungeSettings,
} from '../mfcLogic/lounge'
import { applyReplyMarkup as applyReplyMarkupShared } from '../mfcLogic/message/chatReply'
import { forgetRoom, setPinnedRoomSession } from '../mfcLogic/rooms'
import { isLounge as isRoomLounge } from '../mfcLogic/userProperties/user'
import { isTouchDevice } from '../utilities/browser'
import { routerPush } from '../utilities/router'
import { safeObj } from '../utilities/safe'
import {
    getModalState,
    getRoomsState,
    getScreenState,
    getSettingsState,
    getSlugAndId,
    getUser,
} from './getState'
import { closeFloatingPreview } from './internal/modals'
import { setPinnedRooms, setRoomState } from './internal/rooms'
import './internal/rooms/events'
import {
    generateExtraContentMessage,
    processLoungeMessage,
} from './internal/rooms/helpers'
import { idToSlug, slugToID } from './internal/users'
import { closeFloatingRoom, isFloatingRoomPm } from './modals/state.floater'
import { setScreenOrientation } from './state.screen'
import { toggleSidebarVisibility } from './state.sidebar'
import { registerTimer, unregisterTimer } from './state.timers'
import { initVideo } from './state.video'

export const openRoomState = async (
    slugOrId: string | number,
    options: {
        forceSub?: boolean
        pinned?: boolean
    } = {},
) => {
    const { pinned } = options
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return

    const { id } = user

    setRoomState({
        id,
        room: {
            isPinned: pinned,
            viewing: true,
        },
    })

    setPinnedRoomSession(id, {
        open: pinned,
    })
}

export const openRoom = (
    slugOrId: string | number,
    options: {
        loungeSurrogateID?: number
        pinned?: boolean
        transitionSource?: TransitionType
    } = {},
) => {
    const [userSlug, userID] = getSlugAndId(slugOrId)
    const path =
        `/room/${userSlug}` +
        (options.loungeSurrogateID
            ? '/' + idToSlug[options.loungeSurrogateID]
            : '')
    routerPush(path, {
        transitionSource: options.transitionSource,
    })
    setPinnedRoomSession(userID, {
        open: options.pinned,
    })
}

export const closeRoom = (
    slugOrId: string | number,
    options: {
        destroy?: boolean
        skipCloseIfPinned?: boolean
    } = {},
) => {
    const [, id] = getSlugAndId(slugOrId)
    if (!id) return
    const { destroy, skipCloseIfPinned } = options
    if (destroy) {
        // unpin room
        pinRoom(id, { pinned: false, skipClose: true })
        // close any floating room
        closeFloatingRoom(id, { skipClose: true })
        // remove the room from localStorage
        forgetRoom(id)
    }

    const { pinnedRooms } = ROOMSSTATE
    if (skipCloseIfPinned && pinnedRooms.includes(id)) return

    destroyRoomState(id)
}

export const setLoungeSurrogate = (
    loungeSurrogateID: number,
    loungeIdOrSlug: number | string,
    options?: { skipAddSession?: boolean; skipRoute?: boolean; mute?: boolean },
) => {
    const [, loungeId] = getSlugAndId(loungeIdOrSlug)
    const { skipAddSession, skipRoute = false, ...videoOptions } = options || {}
    const hideVideoSetting =
        getSettingsState()[SettingsType.ROOM]?.hideVideoModelRoom === 1
    if (!hideVideoSetting) initVideo(loungeSurrogateID, videoOptions)

    if (!skipAddSession) {
        addToLoungeRecentModelsSession(loungeSurrogateID)
    }

    startAutoPlayInterval(loungeId)
    if (!skipRoute) setLoungeSurrogateURL(loungeId, loungeSurrogateID)
    return loungeSurrogateID
}

export const setNextSurrogate = (loungeId: number, skipRoute = false) => {
    const { [loungeId]: room = {} } = getRoomsState()
    const { loungeSurrogateID: previousSurrogate } = room
    const nextId = getSafeNextOrPreviousSurrogate(previousSurrogate, {
        shouldGet: true,
    })

    setLoungeSurrogate(nextId, loungeId, { skipRoute })

    return nextId
}

export const setPreviousSurrogate = (loungeId: number) => {
    const { [loungeId]: room = {} } = getRoomsState()
    const { loungeSurrogateID: previousSurrogate } = room
    const previousId = getSafeNextOrPreviousSurrogate(previousSurrogate, {
        previous: true,
        shouldGet: true,
    })

    setLoungeSurrogate(previousId, loungeId)

    return previousId
}

export const startAutoPlayInterval = (loungeId: number) => {
    const { autoPlay, timer } = calculateLoungeSettings(
        getSettingsState()[SettingsType.ROOM]?.loungeAutoplay,
    )
    unregisterTimer('autoPlay')
    if (autoPlay) {
        registerTimer(
            'autoPlay',
            () => {
                setNextSurrogate(loungeId)
            },
            timer * 1000,
        )
    }
    return () => unregisterTimer('autoPlay')
}

export const toggleAutoPlay = (loungeId: number) => {
    const { autoPlay } = calculateLoungeSettings(
        getSettingsState()[SettingsType.ROOM]?.loungeAutoplay,
    )
    saveSettings(SettingsType.ROOM, 'loungeAutoplay', !autoPlay ? 0 : 1)
    if (autoPlay) {
        unregisterTimer('autoPlay')
    } else {
        startAutoPlayInterval(loungeId)
    }
}

export const togglePinRoomChat = (roomId: number) => {
    const room = getRoomsState()[roomId]
    if (!room) return
    setRoomState({
        id: roomId,
        room: {
            showChat: !room.showChat,
        },
    })

    // ensure that we recalc the content area if this is portrait,
    // as toggling chat will change the pins container height
    const { portrait } = getScreenState()
    if (portrait) setScreenOrientation({ overrideSkip: true })
}

export const showPinRoomChat = (roomId: number, showChat = true) => {
    setRoomState({
        id: roomId,
        room: {
            showChat,
        },
    })
}

export const movePinnedRoom = (idToMove: number, indexToMoveTo: number) => {
    const { pinnedRooms } = ROOMSSTATE
    const existingIndex = pinnedRooms.indexOf(idToMove)
    if (existingIndex >= 0) pinnedRooms.splice(existingIndex, 1)
    else pinRoom(idToMove)

    const roomsBefore = pinnedRooms.slice(0, indexToMoveTo)
    const roomsAfter = pinnedRooms.slice(indexToMoveTo)

    const newPinnedRooms = [].concat(roomsBefore, [idToMove], roomsAfter)

    setPinnedRooms(newPinnedRooms)
}

export const pinRoom = (
    roomId: number,
    { pinned = true, skipClose = false } = {},
) => {
    const { pinnedRooms } = ROOMSSTATE

    let nextPinnedRooms = [...pinnedRooms]

    const rooms = getRoomsState()
    if ((pinned && nextPinnedRooms.includes(roomId)) || rooms[roomId]?.isLounge)
        return

    // console.log('[pinRoom]', { roomId, pinned, skipClose })
    const { singlePin } = getScreenState()
    if (singlePin) {
        const [previousRoomId] = pinnedRooms
        nextPinnedRooms = []
        setPinnedRoomSession(previousRoomId, {
            open: false,
        })
        if (pinned) {
            closeFloatingPreview(roomId)
            nextPinnedRooms.push(roomId)
            openRoomState(roomId, { pinned: true })

            setRoomIDState(roomId)
            setRoomState({
                id: roomId,
                room: {
                    isPinned: true,
                    viewing: true,
                },
            })
        } else {
            setPinnedRoomSession(roomId, {
                open: false,
            })
        }

        if (!skipClose) {
            closeRoom(previousRoomId)
        } else {
            setRoomState({
                id: roomId,
                room: {
                    isPinned: false,
                },
            })
        }
        setPinnedRooms(nextPinnedRooms)
        return
    }
    if (pinned) {
        toggleSidebarVisibility(false)
        closeFloatingPreview(roomId)
        nextPinnedRooms.push(roomId)

        openRoomState(roomId, { pinned: true })
        setRoomIDState(roomId)
        setRoomState({
            id: roomId,
            room: {
                isPinned: true,
                viewing: true,
            },
        })
        setPinnedRooms(nextPinnedRooms)
    } else {
        const index = nextPinnedRooms.indexOf(roomId)
        nextPinnedRooms.splice(index, 1)
        if (index >= 0) {
            setPinnedRoomSession(roomId, {
                open: false,
            })
        }
        if (!skipClose) {
            // check to see if we've got the room open, and auto skip if so
            const floatingRoom = getModalState().floating_room
            if (!floatingRoom.show || floatingRoom.id !== roomId)
                closeRoom(roomId)
            else
                setRoomState({
                    id: roomId,
                    room: {
                        isPinned: false,
                    },
                })
        } else {
            setRoomState({
                id: roomId,
                room: {
                    isPinned: false,
                },
            })
        }
        setPinnedRooms(nextPinnedRooms)
    }
}

export const isModelViewable = (id: number) => {
    const model = getUser(id)
    const inLounge = isRoomLounge(id)
    const loungeSurrogateID = inLounge
        ? getRoomsState()[id]?.loungeSurrogateID
        : null
    const {
        isBroadcasting,
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isKicked,
        isOnline,
        banned,
    } = safeObj(model)
    const isPM = isFloatingRoomPm(id)
    // if its a pm and we're not on mobile, then we have quick reply spawn rules
    if (isPM && !isTouchDevice) {
        if (isOnline) return true
        return false
    }
    // be generous with lounge check
    if (inLounge && loungeSurrogateID) return true
    if (isTruePrivate || isPrivateShow || isGroupShow || isClubShow) return true

    if (!isBroadcasting) return false
    if (isKicked || banned) return false
    return true
}

export const resolveShowExitMessages = (
    id: number,
    previousStreamType: StreamType,
) => {
    const showExitMessage = {
        text: `You have exited the ${previousStreamType} show.`,
        timestamp: Date.now(),
        endShow: true,
        system: true,
    }

    setRoomMessagesState(showExitMessage, id)
}

const removeExistingExtraContent = (
    messages: Array<Message>,
    options: { topicUpdate?: boolean } = {},
) => {
    const { topicUpdate } = options

    if (!messages || !topicUpdate) {
        return messages
    }

    return messages.filter((message) => !message.extraContent)
}

const removeExistingClubShowMessage = (id: number) => {
    const { [id]: existingRoom } = getRoomsState()

    if (!existingRoom) {
        return
    }

    const { messages: existingMessages = [] } = existingRoom

    const filteredMessages = existingMessages.filter(
        (message) => !message.clubShow,
    )

    if (filteredMessages.length === existingMessages.length) {
        return
    }
    setRoomState({ id, room: { messages: filteredMessages } })
}

export const removeEndOfShowMessage = (id: number) => {
    const { [id]: existingRoom } = getRoomsState()

    if (!existingRoom) {
        return
    }

    const { messages: existingMessages } = existingRoom

    if (!existingMessages) {
        return
    }

    const filteredMessages = existingMessages.filter(
        (message) => !message.endShow,
    )

    if (filteredMessages.length === existingMessages.length) {
        return
    }
    setRoomState({ id, room: { messages: filteredMessages } })
}

const reorderEndMessage = (messages: Array<Message>) => {
    const exitMessage = messages.find((message) => message.endShow)

    if (!exitMessage) {
        return null
    }

    const filteredMessages = messages.filter((message) => !message.endShow)
    const welcomMessageIndex = filteredMessages.findIndex(
        (message) => message.welcome,
    )

    if (welcomMessageIndex === -1) {
        return null
    }

    return [
        ...filteredMessages.slice(0, welcomMessageIndex),
        exitMessage,
        filteredMessages[welcomMessageIndex],
        ...filteredMessages.slice(welcomMessageIndex + 1),
    ]
}

const reorderMessage = (
    messages: Array<Message>,
    filter: (value: Message) => unknown,
    skipLast = false,
) => {
    const messageIndex = messages.findIndex(filter)

    if (messageIndex === -1) {
        return null
    }

    const existingMessage = messages[messageIndex]
    const filteredMessages = messages.filter((message) => !filter(message))

    if (skipLast) {
        return [...filteredMessages, existingMessage]
    }

    return [
        ...filteredMessages.slice(0, -1),
        existingMessage,
        filteredMessages[filteredMessages.length - 1],
    ]
}

export const resolveClubShowMessage = (isClubShow: boolean, id: number) => {
    const { [id]: room } = getRoomsState()

    if (!room) {
        return
    }

    removeExistingClubShowMessage(id)

    if (!isClubShow) {
        return
    }

    const message: Message = {
        clubShow: true,
        system: true,
        from: id,
    }

    setRoomMessagesState(message, id)
}

// private function
export const _setRoomOpenState = (isOpen: boolean, id: number) => {
    const { [id]: existingRoomState } = getRoomsState()

    if (!existingRoomState) {
        return
    }
    setRoomState({ id, room: { isOpen } })
}

export const setRoomLoungeState = (isLounge: boolean, id: number) =>
    setRoomState({ id, room: { isLounge } })

export const setRoomLoungeSurrogateState = (
    loungeSurrogateID: number,
    id: number,
) => {
    const { [id]: existingRoomState } = getRoomsState()

    if (!existingRoomState) {
        return
    }

    const { loungeSurrogateID: previousLoungeSurrogateID } = existingRoomState

    if (previousLoungeSurrogateID === loungeSurrogateID) {
        //
        // This condition happens only when navigating to a lounge with a
        // model already specified.
        //
        return
    }
    setRoomState({
        id,
        room: {
            previousLoungeSurrogateID,
            loungeSurrogateID,
            isLounge: true,
        },
    })
}

export const setRoomIDState = (id: number) => {
    // console.log(`[app-state: room ${id}] SETTING ROOM ID`)

    const {
        users: { [id]: user },
    } = USERSSTATE

    if (!user) {
        return
    }

    const { streamType } = user

    if (!streamType) {
        streamPublicShow(id)
    }
    setRoomState({ id, room: { id } }, { immediate: true })
}

const applyReplyMarkup = (message: Message): Message =>
    applyReplyMarkupShared(message, {
        resolveUserId: (username) => slugToID[username.toLowerCase()],
        resolveModelName: (id) => USERSSTATE.users[id]?.name,
    })

export const setRoomMessagesState = async (message: Message, id: number) => {
    const { [id]: existingRoom } = getRoomsState()

    if (!existingRoom) {
        return
    }

    const { isLounge } = existingRoom

    //
    // Marc: If messages are stale due to socket being disconnected, then clear
    // them. They were only a placeholder until the new ones came in.
    //

    const { topic: topicUpdate, welcome } = message
    // Reply parsing must run before lounge processing: in the lounge,
    // `processLoungeMessage` rewrites the leading username (e.g. `Pindeco`)
    // into a `{{model|<id>}}` token, which would prevent parseReply's regex
    // from matching the wire prefix.
    const replyMarkedMessage = applyReplyMarkup(message)
    const [processedMessage, processedPreviews] = await processLoungeMessage(
        replyMarkedMessage,
        isLounge,
    )

    // get the latest room state after the async operation (to avoid race conditions)
    const {
        messages: existingMessages = [],
        areMessagesStale,
        // ensure loungePreviews are set to an empty array if we're in a lounge
        loungePreviews: existingPreviews = [],
    } = getRoomsState()[id] ?? {}

    if (areMessagesStale) {
        setRoomMessagesStaleState(false, id)
    }

    const messages = [
        ...removeExistingExtraContent(
            areMessagesStale ? [] : existingMessages,
            { topicUpdate },
        ),
        processedMessage,
    ]
    const model = USERSSTATE.users[id]
    const { isClubShow, streamType, topic } = model
    const hasTopic = !!topic

    if (
        ((welcome && !hasTopic) || topicUpdate) &&
        SECURESTATE.ageVerification.ageGateActive === false
    ) {
        messages.push(generateExtraContentMessage(USERSSTATE.users[id]))
    }

    if (welcome || topicUpdate) {
        //
        //
        // Marc: This individual reordering thing has gotten kind of silly. The
        // correct thing to do would likely be to make a function that looks for
        // "system: true" and reorder all messages at once.
        //
        //
        const reorederedEndShowMessages = reorderEndMessage(messages)

        const reorederedVoyeurMessages = reorderMessage(
            reorederedEndShowMessages || messages,
            (message) => message.voyeur,
        )

        const reorederedGrupshowMessages = reorderMessage(
            reorederedVoyeurMessages || reorederedEndShowMessages || messages,
            (message) => message.groupShow,
        )

        //
        // Marc: lol... What a chain.
        //
        const reorederedClubshowMessages = reorderMessage(
            reorederedGrupshowMessages ||
                reorederedVoyeurMessages ||
                reorederedEndShowMessages ||
                messages,
            (message) => message.clubShow,
            true,
        )

        const reorederedMessages =
            reorederedClubshowMessages ||
            reorederedGrupshowMessages ||
            reorederedVoyeurMessages ||
            reorederedEndShowMessages

        if (reorederedMessages) {
            // clearing out existing messages
            messages.length = 0
            messages.push(...reorederedMessages)
        }
    }

    const trimmedMessages =
        messages.length > 250 ? messages.slice(messages.length - 250) : messages

    if (processedPreviews.length > 0) {
        let hasNew = false

        let trimmedPreviews = existingPreviews.slice(0, 24)
        processedPreviews.forEach((processed) => {
            if (
                !trimmedPreviews.find(
                    (existingPreview) => existingPreview.id === processed.id,
                )
            ) {
                hasNew = true
                trimmedPreviews = [
                    {
                        id: processed.id,
                        slug: processed.slug,
                        sender: message.from,
                        timestamp: message.timestamp,
                        message: processedMessage.text
                            .replaceAll(/\{\{([^]+)\}\}/g, '')
                            .replaceAll(/:\d+:/g, '')
                            .trim(),
                    },
                    ...trimmedPreviews,
                ]
            }
        })
        setRoomState({
            id,
            room: hasNew
                ? {
                      messages: trimmedMessages,
                      loungePreviews: trimmedPreviews,
                  }
                : { messages: trimmedMessages },
        })
    } else {
        setRoomState({
            id,
            room: {
                messages: trimmedMessages,
            },
        })
    }

    if (welcome && streamType === 'public') {
        resolveClubShowMessage(isClubShow, id)
    }
}

export const setRoomMessagesStaleState = (stale, id) =>
    setRoomState({ id, room: { areMessagesStale: stale } }, { immediate: true })

export const clearRoomMessagesState = (id: number) => {
    const { [id]: existingRoomState } = getRoomsState()

    if (!existingRoomState) {
        return
    }
    setRoomState({ id, room: { messages: [] } })
}

export const setRoomPendingReply = (
    id: number,
    pendingReply: { userId: number; username: string; quoted: string },
) => {
    const { [id]: existingRoom } = getRoomsState()
    if (!existingRoom) {
        return
    }
    setRoomState({ id, room: { pendingReply } })
}

export const clearRoomPendingReply = (id: number) => {
    const { [id]: existingRoom } = getRoomsState()
    if (!existingRoom || !existingRoom.pendingReply) {
        return
    }
    setRoomState({ id, room: { pendingReply: undefined } })
}

export const setRoomTipState = (tipMessage: TipMessage, id: number) => {
    const { [id]: existingRoom } = getRoomsState()
    if (!existingRoom) {
        return
    }
    const { tips = [] } = existingRoom
    tips.push(tipMessage)
    setRoomState({ id, room: { tips: [...tips] } })
}

export const clearRoomTipState = (id: number) => {
    const { [id]: existingRoom } = getRoomsState()
    if (!existingRoom) {
        return
    }
    setRoomState({ id, room: { tips: [] } })
}

export const markRoomTipsAsDisplayed = (id: number) => {
    const { [id]: existingRoom } = getRoomsState()
    if (!existingRoom) {
        return
    }
    const { tips = [] } = existingRoom
    const clearedTips = tips.map((tip) => {
        tip.displayed = true
        return tip
    })
    setRoomState({ id, room: { tips: clearedTips } })
}

export const destroyRoomState = (id: number) => {
    // console.log(`[app-state: room ${id}] DESTROYING ROOM`)

    const { [id]: existingRoomState } = getRoomsState()

    if (!existingRoomState) {
        return
    }
    setRoomState({ id, room: null }, { deleteRoom: true, immediate: true })

    unsubscribeRoom(id)
}

export const setRoomParticipantsState = (
    participants: Array<Participant>,
    id: number,
) =>
    setRoomState(
        { id, room: { participants: participants } },
        { immediate: true },
    )

export const setRoomInsertParticipantState = (
    participant: Participant,
    id: number,
) => {
    const { [id]: room } = getRoomsState()

    if (!room) {
        return
    }

    // console.log('[app-state: participants] INSERT:', participantID)

    const { participants: existingParticipants = [] } = room

    const updatedParticipants =
        participant.id === id
            ? [participant, ...existingParticipants]
            : [...existingParticipants, participant]

    if (
        process.env.NODE_ENV === 'development' &&
        Array.from(new Set(updatedParticipants)).length !==
            updatedParticipants.length
    ) {
        console.error(
            '[app-state: participants] Found duplicate participants. This is not supposed to happen. Please check.',
            JSON.stringify(
                { existingParticipants, updatedParticipants, participant },
                null,
                4,
            ),
        )
    }
    setRoomState({ id, room: { participants: updatedParticipants } })
}

export const setRoomRemoveParticipantState = (
    participantID: number,
    id: number,
) => {
    const { [id]: room } = getRoomsState()

    if (!room) {
        return
    }

    // console.log('[app-state: participants] REMOVE:', participantID)

    const { participants: existingParticipants = [] } = room

    const updatedParticipants = existingParticipants.filter(
        ({ id }) => participantID !== id,
    )

    if (
        process.env.NODE_ENV === 'development' &&
        existingParticipants.length === updatedParticipants.length
    ) {
        console.error(
            '[app-state: participants] Some how a participant was not removed. This is not supposed to happen. Please check.',
            JSON.stringify(
                { existingParticipants, updatedParticipants, participantID },
                null,
                4,
            ),
        )
    }

    setRoomState({ id, room: { participants: updatedParticipants } })
}

export const setRoomGuestCount = (guestCount: number, id: number) => {
    setRoomState({ id, room: { guestCount } })
}

export const setRoomNewsMessage = (message: Message) => {
    const rooms = getRoomsState()

    Object.values(rooms).forEach(({ isOpen, id }) => {
        if (isOpen) {
            setRoomMessagesState({ ...message, system: true }, id)
        }
    })
}

export const clearRoomMessagesBySid = (
    id: number,
    sids: Array<number>,
    uids: Array<number>,
    min?: number,
) => {
    const { [id]: existingRoom } = getRoomsState()

    if (!existingRoom) {
        return
    }

    const { messages: existingMessages = [] } = existingRoom

    // calculate the cutoff timestamp (min is in minutes)
    const afterStamp = min ? Date.now() - min * 60 * 1000 : 0

    const sidsSet = new Set(sids)
    const uidsSet = new Set(uids)

    const filteredMessages = existingMessages.filter((message) => {
        // keep messages older than the cutoff
        if (message.timestamp && message.timestamp < afterStamp) {
            return true
        }

        // remove if sid matches
        if (message.sid && sidsSet.has(message.sid)) {
            return false
        }

        // remove if uid (from) matches
        if (message.from && uidsSet.has(message.from)) {
            return false
        }

        return true
    })

    // only update if we actually removed something
    if (filteredMessages.length !== existingMessages.length) {
        setRoomState({ id, room: { messages: filteredMessages } })
    }
}
